import zipcode_to_timezone from 'zipcode-to-timezone';

// useMutation with onError resolves `{ errors }` instead of throwing, so check the result.
export const mutationResult = (res) => (!res || res.errors || !res.data ? null : res.data);

// --- Zips ---

// ZIP+4 is accepted and cut to 5 digits: subregions use 5-digit ZCTAs.
const ZIP_FORMAT = /^(\d{5})(?:-\d{4})?$/;

// Commas or whitespace, so a pasted column works.
export const parseZipInput = (raw) =>
    (raw || '')
        .split(/[\s,]+/)
        .map((zip) => zip.trim())
        .filter(Boolean);

// zipcode-to-timezone is our only zip reference. Gaps: PR / USVI, Guam, APO/FPO and post-2016
// zips (map clicks skip this check).
export const isKnownZip = (zip) => !!zipcode_to_timezone.lookup(zip);

// A bad entry never blocks the batch. `invalid`: bad format; `unrecognized`: not a real zip;
// `duplicate`: already on the subregion (dropped quietly).
export const partitionZipInput = (raw, existingZips = []) => {
    const existing = new Set(existingZips);
    const seen = new Set();
    // Pushed into: pastes can run to thousands.
    const result = { added: [], invalid: [], unrecognized: [], duplicate: [] };

    parseZipInput(raw).forEach((entry) => {
        const match = ZIP_FORMAT.exec(entry);
        if (!match) {
            result.invalid.push(entry);
            return;
        }
        // Normalized, so 90210 and 90210-1234 dedupe.
        const zip = match[1];
        // Report a repeat once.
        if (seen.has(zip)) return;
        seen.add(zip);
        if (!isKnownZip(zip)) result.unrecognized.push(zip);
        else if (existing.has(zip)) result.duplicate.push(zip);
        else result.added.push(zip);
    });

    return result;
};

// A ZCTA split across tiles returns several shards, and a boundary click several zips; the first
// is the one aimed at.
export const zipFromFeatures = (features) =>
    (features || []).map((feature) => feature?.properties?.NAME20).find((zip) => !!zip) || null;

// Array rather than Set: the draft goes straight into a mapbox filter.
export const toggleZip = (zips, zip) => {
    if (!zip) return zips;
    return zips.includes(zip) ? zips.filter((existing) => existing !== zip) : [...zips, zip];
};

// Diffed against saved state, so remove-then-re-add writes nothing. `toRemove` is mappings:
// deletes key off mapping_id.
export const diffDraftZips = (savedMappings = [], draftZips = []) => {
    const draft = new Set(draftZips);
    const saved = new Set(savedMappings.map((mapping) => mapping.zip));

    return {
        toAdd: draftZips.filter((zip) => !saved.has(zip)),
        toRemove: savedMappings.filter((mapping) => !draft.has(mapping.zip)),
    };
};

// Overlaps are legal but warned about: autoAssign has no order_by, so an overlapping zip gets an
// arbitrary carrier. Labels fall back to subregion_id.
export const zipOverlapIndex = (subregions = [], currentSubregionId = null) => {
    const index = {};

    (subregions || []).forEach((subregion) => {
        if (subregion.subregion_id === currentSubregionId) return;

        const label = subregion.name || subregion.subregion_id;
        (subregion.zips || []).forEach(({ zip }) => {
            (index[zip] = index[zip] || []).push(label);
        });
    });

    return index;
};

// Order-insensitive: the draft is a list, but membership is what it means.
export const sameZipSet = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((zip) => set.has(zip));
};

// Folds a write made while the draft was open (Assign popover, inline rename) into it. Reseeding
// would lose edits; ignoring it would make Save delete the new zip.
export const reconcileDraft = (draft, saved) => {
    const externallyRemoved = new Set(draft.base.zips.filter((zip) => !saved.zips.includes(zip)));
    const inDraft = new Set(draft.zips);
    const externallyAdded = saved.zips.filter((zip) => !draft.base.zips.includes(zip) && !inDraft.has(zip));

    return {
        ...draft,
        // An untouched name follows the saved value; one the user typed wins.
        name: draft.name === draft.base.name ? saved.name : draft.name,
        zips: [...draft.zips.filter((zip) => !externallyRemoved.has(zip)), ...externallyAdded],
        base: saved,
    };
};

export const overlapMessage = (names = []) => {
    if (!names.length) return null;
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return `This zip is already applied to the ${list} ${names.length === 1 ? 'subregion' : 'subregions'}`;
};

// --- Carrier + settings edits: stored over saved state, so nothing needs reconciling ---

export const EMPTY_CARRIER_EDITS = { added: [], removed: [], changed: {} };

// Saved rows key off mapping_id, draft rows off draft_key.
export const carrierKey = (mapping) => mapping?.mapping_id || mapping?.draft_key;

// Primary first; the other carriers keep their order.
export const byPrimary = (a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0);

// The carriers as the draft has them, primary first. Added rows follow the saved ones.
export const applyCarrierEdits = (saved = [], edits = EMPTY_CARRIER_EDITS) => {
    const removed = new Set(edits.removed);
    return [
        ...saved
            .filter((m) => !removed.has(m.mapping_id))
            .map((m) => (edits.changed[m.mapping_id] ? { ...m, ...edits.changed[m.mapping_id] } : m)),
        ...edits.added,
    ].sort(byPrimary);
};

// Switches write booleans, but the column can be NULL.
const sameSetting = (saved, next) =>
    typeof next === 'boolean' ? !!saved === next : (saved ?? null) === (next ?? null);

// Only keys that differ, so toggling back isn't dirty.
export const settingsChanges = (subregion, patch = {}) =>
    Object.fromEntries(Object.entries(patch).filter(([key, value]) => !sameSetting(subregion?.[key], value)));
